import api, { fromV2 } from "./Api";
import config from "./config";
import { topicUrl } from "./utils";
import type { Subscription, Notification } from "./types";

export const ConnectionState = {
  Connected: "connected",
  Connecting: "connecting",
};

type StateListener = (subscriptionId: string, state: string) => void;
type MessageListener = (subscriptionId: string, notification: Notification) => void;
type SyncListener = () => void;

const NOTIFICATION_EVENTS = ["message", "message_delete", "message_clear"];

/**
 * 구독한 토픽 전부를 SSE 스트림 하나(GET /api/v2/stream?topics=a,b)로 받는다 — 예전 토픽마다 웹소켓을 대신한다.
 * 끊기면 브라우저 EventSource 가 Last-Event-ID 로 알아서 이어 받는다(서버가 retry 를 준다).
 * 토픽 목록이 바뀌면 지금까지 받은 마지막 id 부터 새로 연다. 처음 열 때는 백로그 없이 열고, 놓친 것은 Poller 가 채운다.
 * `sync` 이벤트(다른 기기에서 구독·설정 변경)는 syncListener 로 넘긴다(AccountApi.sync).
 */
class ConnectionManager {
  source: EventSource | null = null;

  topicsKey = "";

  subscriptionIds: string[] = [];

  lastEventId: string | null = null;

  stateListener: StateListener | null = null;

  messageListener: MessageListener | null = null;

  syncListener: SyncListener | null = null;

  registerStateListener(listener: StateListener) {
    this.stateListener = listener;
  }

  resetStateListener() {
    this.stateListener = null;
  }

  registerMessageListener(listener: MessageListener) {
    this.messageListener = listener;
  }

  resetMessageListener() {
    this.messageListener = null;
  }

  registerSyncListener(listener: SyncListener) {
    this.syncListener = listener;
  }

  resetSyncListener() {
    this.syncListener = null;
  }

  refresh(subscriptions: Subscription[] | null | undefined) {
    if (!subscriptions) {
      return;
    }
    const local = subscriptions.filter((s) => !s.internal && s.baseUrl === config.base_url);
    const topics = [...new Set(local.map((s) => s.topic))].sort();
    const key = topics.join(",");
    this.subscriptionIds = local.map((s) => s.id);
    if (key === this.topicsKey && this.source) {
      return;
    }
    this.close();
    this.topicsKey = key;
    if (topics.length === 0) {
      return;
    }
    const url = api.streamUrl(topics, this.lastEventId);
    console.log(`[ConnectionManager] Opening stream ${url}`);
    const source = new EventSource(url, { withCredentials: true });
    this.source = source;
    this.setAllStates(ConnectionState.Connecting);
    source.onopen = () => {
      console.log(`[ConnectionManager] Stream open`);
      this.setAllStates(ConnectionState.Connected);
    };
    source.onerror = () => {
      // readyState CONNECTING = 브라우저가 다시 붙는 중, CLOSED = 포기(401 등) — 그때는 잠시 뒤 직접 다시 연다
      this.setAllStates(ConnectionState.Connecting);
      if (source.readyState === EventSource.CLOSED && this.source === source) {
        this.source = null;
        setTimeout(() => {
          if (!this.source && this.topicsKey === key) {
            this.topicsKey = "";
            this.refresh(subscriptions);
          }
        }, 10000);
      }
    };
    NOTIFICATION_EVENTS.forEach((type) =>
      source.addEventListener(type, (ev) => {
        const event = ev as MessageEvent<string>;
        try {
          const notification = fromV2(JSON.parse(event.data));
          this.lastEventId = event.lastEventId || notification.id;
          this.notificationReceived(topicUrl(config.base_url, notification.topic!), notification);
        } catch (e) {
          console.log(`[ConnectionManager] Error handling ${type} event`, e);
        }
      })
    );
    source.addEventListener("sync", () => {
      console.log(`[ConnectionManager] Sync event`);
      this.syncListener?.();
    });
  }

  close() {
    if (this.source) {
      console.log(`[ConnectionManager] Closing stream`);
      this.source.close();
      this.source = null;
    }
  }

  setAllStates(state: string) {
    this.subscriptionIds.forEach((id) => this.stateChanged(id, state));
  }

  stateChanged(subscriptionId: string, state: string) {
    try {
      this.stateListener?.(subscriptionId, state);
    } catch (e) {
      console.error(`[ConnectionManager] Error updating state of ${subscriptionId} to ${state}`, e);
    }
  }

  notificationReceived(subscriptionId: string, notification: Notification) {
    try {
      this.messageListener?.(subscriptionId, notification);
    } catch (e) {
      console.error(`[ConnectionManager] Error handling notification for ${subscriptionId}`, e);
    }
  }
}

const connectionManager = new ConnectionManager();
export default connectionManager;
